/// This function is uncallable in TypeScript, so the compiler will complain if it is called.
/// It can thus be used to make switches and checks exhaustive, by passing the checked value in here in the default case.
import { R } from '@/shared/EnumResources.resx';
import { Log } from '@/shared/Log';

export function assertExhausted(unreachable: never) {}

export function getEnumTranslationByKey<T>(
    enumObj: T,
    value: T[keyof T],
): string {
    const key = (Object.keys(enumObj) as Array<keyof T>).find(
        (key) => enumObj[key] === value,
    ) as string;
    const fullKey = `${getEnumName(enumObj)}_${key}`;
    const result = getResourceForEnum(fullKey);
    if (result === fullKey) {
        Log.error('Cannot find enum for key', fullKey);
    }
    return result;
}

export function getEnumKeys<T extends Record<string, string | number>>(
    enumObj: T,
): (keyof T)[] {
    return Object.keys(enumObj).filter((key) =>
        isNaN(Number(key)),
    ) as (keyof T)[];
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

export enum ConsultationTypeEnum {
    Message = 0,
    Video = 1,
    Prescription = 2,
    MidwifeMessage = 3,
    MidwifeVideo = 4,
    NurseMessage = 5,
    NurseVideo = 6,
    DentistMessage = 7,
    DentistVideo = 8,
    DermatologistMessage = 9,
    DermatologistVideo = 10,
    RheumatologistVideo = 11,
    PhysiotherapistStartupVideo = 12,
    PsychologistVideo = 13,
    AllergyCheckVideo = 14,
    SundVaegtStartupMessage = 15,
    SundVaegtNurseCheckupVideo = 16,
    SundVaegtDoctorCheckupVideo = 17,
    SundVaegtNurseCheckupMessage = 18,
    SundVaegtNurseAdHocVideo = 19,
    SundVaegtQuestionnaireMessage = 20,
    PhysiotherapistMessage = 21,
    PhysiotherapistCheckupVideo = 22,
    PhysiotherapistQuestionnaireMessage = 23,
    DietitianStartupVideo = 24,
    DietitianCheckupVideo = 25,
    PsychologistCrisisVideo = 26,
    PersonalHealthProgrammeStartup = 27,
    AcneProgrammeStartupMessage = 28,
    AcneProgrammeStartupVideo = 29,
    AcneProgrammeCheckupVideo = 30,
    AcneDermatologistMessage = 31,
    VeterinarianVideo = 32,
    DietitianUploadAttachmentMessage = 33,
}

export enum UserTypeEnum {
    Patient = 0,
    Doctor = 1,
    Backend = 2,
}

export enum PetTypeEnum {
    Dog = 0,
    Cat = 1,
    Rabbit = 2,
    Rodent = 3,
}

export enum VetConsultationReasonEnum {
    Gastrointestinal = 0,
    SkinCoatEars = 1,
    Poisoning = 2,
    Eyes = 3,
    UrinaryTract = 4,
    Lameness = 5,
    RespiratoryTract = 6,
    OralCavity = 7,
    Pain = 8,
    Claws = 9,
    GenitalsMammaryGlands = 10,
    ReproductionCycle = 11,
    Behaviour = 12,
    Prescription = 13,
    OtherReason = 1000,
}

export enum VetConsultationOutcomeEnum {
    AdvisedForHomeTreatment = 0,
    ReferredDuringRegularOpeningHours = 1,
    ReferredOutsideRegularOpeningHours = 2,
}

export enum PetSexEnum {
    Female,
    FemaleSterilized,
    Male,
    MaleNeutered,
    Unknown,
}

export enum CaregiverRoleEnum {
    Doctor = 0,
    Nurse = 1,
    Dentist = 2,
    Midwife = 3,
    Dermatologist = 4,
    Rheumatologist = 5,
    Physiotherapist = 6,
    Psychologist = 7,
    AllergyDoctor = 8,
    SundVaegtDoctor = 9,
    SundVaegtNurse = 10,
    Dietitian = 11,
    AcneDermatologist = 12,
    Veterinarian = 13,
}

export enum PartnerStatusEnum {
    NoPartner = 0,
    Invited = 1,
    Active = 2,
}

export enum SignupDoctorResponseEnum {
    OK,
    Failed,
    EmailExists,
}

export enum OpenVideoConsultationStatusEnum {
    OK = 0,
    NoDoctorsAvailable = 1,
    TimeSlotOverbooked = 2,
    BadRequest = 3,
    PrerequisiteExists = 4,
    TooLateToReschedule = 5,
}

export enum ServiceTypeEnum {
    VideoConsultation = 0,
    Message = 1,
    MessageReply = 2,
    Prescription = 3,
    DermatologistMessage = 4,
    DermatologistMessageReply = 5,
    RheumatologistVideo = 6,
    PhysiotherapistStartupVideo = 7,
    DermatologistVideo = 8,
    PsychologistVideo = 9,
    AllergyCheckVideo = 10,
    SundVaegtStartupMessage = 11,
    SundVaegtNurseCheckupVideo = 12,
    SundVaegtDoctorCheckupVideo = 13,
    SundVaegtNurseCheckupMessage = 14,
    SundVaegtNurseCheckupMessageReply = 15,
    SundVaegtNurseAdHocVideo = 16,
    SundVaegtQuestionnaireMessage = 17,
    PhysiotherapistMessage = 18,
    PhysiotherapistMessageReply = 19,
    PhysiotherapistCheckupVideo = 20,
    SundVaegtQuestionnaireMessageReply = 21,
    PhysiotherapistQuestionnaireMessage = 22,
    SundVaegtStartupMessageReply = 23,
    DietitianStartupVideo = 24,
    DietitianCheckupVideo = 25,
    PsychologistCrisisVideo = 26,
    PersonalHealthProgrammeStartup = 27,
    AcneProgrammeStartupMessage = 28,
    AcneProgrammeStartupVideo = 29,
    AcneProgrammeCheckupVideo = 30,
    AcneDermatologistMessage = 31,
    AcneDermatologistMessageReply = 32,
    VeterinarianVideo = 33,
}

export enum OrderStatusEnum {
    ProcessingInEpay = 1,
    ProcessingInLaegecheck = 2,
    Completed = 3,
    Canceled = 4,
}

export enum AvailableRelationTypeEnum {
    Child = 0,
    Partner = 1,
    Himself = 2,
}

export enum DeviceTypeEnum {
    Browser = 0,
    iOS = 1,
    Android = 2,
}

export enum PaymentProvider {
    Company = 0,
    AppStore = 1,
    GoogleWallet = 2,
    ePay = 3,
    DIBS = 4,
    Subscription = 5,
    Coupon = 6,
    ReepayCheckout = 7,
    GracePeriod = 8,
    PriceOverride = 9,
}

export enum InsurancePolicyProviderType {
    None = 0,
    Falck = 1,
    Dss = 2,
    Lego = 3,
    If = 4,
    FalckHealthcare = 5,
    ApPension = 6,
    Pfa = 7,
    Codan = 8,
    CodanIndboforsikring = 9,
    Privatsikring = 10,
    Popermo = 11,
    AlmBrand = 12,
    Langelandsfestival = 13,
    Bdo = 14,
    Kf = 15,
    Jyske = 16,
    Vestjylland = 17,
    Forsia = 18,
    Lb = 19,
    Bauta = 20,
    Runa = 21,
    LbPfa = 22,
    DetFagligeHusForsikring = 23,
    EuroAccident = 24,
    DjoefForsikring = 25,
    PPlus = 26,
}

export enum SubscriptionStatusEnum {
    Trialing = 0,
    Active = 1,
    PastDue = 2,
    Canceled = 3,
    Expired = 4,
    Unknown = 2147483647,
}

export enum PlanInterval {
    Day,
    Week,
    Month,
    Year,
}

export enum PhoneCallRejectedReason {
    ConsultationIsAlreadyTaken = 'ConsultationIsAlreadyTaken',
}

// Enums and their translations have to be kept in Models/EnumResources.resx as the file present here is a copy for reference to generate files for translations.
const enumNameMap = new Map<object, string>([
    [PaymentProvider, 'ConsultationPaymentProviderEnum'],
    [PetSexEnum, 'PetSex'],
    [PetTypeEnum, 'PetType'],
    [SubscriptionStatusEnum, 'SubscriptionStatusEnum'],
    [UserTypeEnum, 'UserTypeEnum'],
    [ServiceTypeEnum, 'ServiceTypeEnum'],
    [PlanInterval, 'PlanInterval'],
    [ConsultationTypeEnum, 'ConsultationTypeEnum'],
    [CaregiverRoleEnum, 'CaregiverRoleEnum'],
    [VetConsultationOutcomeEnum, 'VetConsultationOutcomeEnum'],
    [VetConsultationReasonEnum, 'VetConsultationReasonEnum'],
]);
function getResourceForEnum(value: string) {
    return R[value as keyof typeof R] ?? value;
}
function getEnumName<T>(enumObj: T): string | undefined {
    return enumNameMap.get(enumObj as object);
}
